import React, { useState, useEffect, useMemo } from "react";
import AdvancedRoofBidderHeader from "../AdvancedRoofBidderHeader";
import { getBidsByJob, getJob, getInstallerProfile } from "../../lib/data";
import { supabase } from "../../lib/supabase";
import SortFilterControls from "../CompareBids/SortFilterControls";
import PropertyDetailsCard from "../CompareBids/PropertyDetailsCard";
import BidCard from "../CompareBids/BidCard";
import NoBidsEmptyState from "../CompareBids/NoBidsEmptyState";

const CompareBidsPage = ({ user, onClose }) => {
  const [bids, setBids] = useState([]);
  const [sortBy, setSortBy] = useState("price");
  const [filterBy, setFilterBy] = useState("all");
  const [roofData, setRoofData] = useState(null);
  const [mode, setMode] = useState("demo"); // demo | job
  const [selectedBid, setSelectedBid] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const jobId = params.get("jobId");

  useEffect(() => {
    if (jobId) {
      // Installer-submitted bids for a job
      const job = getJob(jobId);
      if (job) {
        setMode("job");
        setRoofData({
          address: job.address,
          totalArea: `${Number(job.squareFeet || 0).toLocaleString()} sq ft`,
          pitchedArea: `${Number(
            job.pitchedArea || Math.round(job.squareFeet * 0.8)
          ).toLocaleString()} sq ft`,
          flatArea: `${Number(
            job.flatArea || Math.round(job.squareFeet * 0.2)
          ).toLocaleString()} sq ft`,
          roofType: job.roofType,
          detectionMethod: job.detectionMethod,
          confidence: job.confidence,
        });

        const raw = getBidsByJob(jobId);

        // Start from local/demo profile so app still works without Supabase
        const baseProfilesByInstaller = new Map();
        raw.forEach((b) => {
          if (!baseProfilesByInstaller.has(b.installerId)) {
            baseProfilesByInstaller.set(
              b.installerId,
              getInstallerProfile(b.installerId)
            );
          }
        });

        // If Supabase is configured, hydrate with real profiles from `profiles` table
        const enhanceWithSupabase = async () => {
          let supabaseProfilesById = new Map();
          if (supabase) {
            try {
              const installerIds = Array.from(baseProfilesByInstaller.keys());
              if (installerIds.length) {
                const { data, error } = await supabase
                  .from("profiles")
                  .select("*")
                  .in("id", installerIds);
                if (!error && data) {
                  supabaseProfilesById = new Map(
                    data.map((row) => [row.id, row])
                  );
                }
              }
            } catch (e) {
              // Fail quietly; we’ll fall back to local/demo data
              console.warn(
                "Failed to hydrate installer profiles from Supabase",
                e
              );
            }
          }

          const transformed = raw.map((b) => {
            const baseProfile =
              baseProfilesByInstaller.get(b.installerId) || {};
            const supa = supabaseProfilesById.get(b.installerId) || {};

            const mergedProfile = {
              ...baseProfile,
              // Map Supabase fields onto our profile shape without breaking demo
              companyName: supa.company_name || baseProfile.companyName,
              contactName: supa.name || baseProfile.contactName,
              phone: supa.phone || baseProfile.phone,
              email: supa.email || baseProfile.email,
              address: supa.address || baseProfile.address,
              serviceAreas: supa.service_areas || baseProfile.serviceAreas,
              rating: supa.rating ?? baseProfile.rating,
              reviews: supa.reviews ?? baseProfile.reviews,
              verified: supa.verified ?? baseProfile.verified,
              experienceYears:
                supa.experience_years ?? baseProfile.experienceYears,
              specialties: supa.specialties || baseProfile.specialties,
              avatar: supa.avatar || baseProfile.avatar,
              completedJobs: supa.completed_jobs ?? baseProfile.completedJobs,
              baseRateSqFt: baseProfile.baseRateSqFt,
              baseTimeline: baseProfile.baseTimeline,
              whatsIncluded: baseProfile.whatsIncluded,
            };

            const totalSqFt = job.squareFeet || 1;
            const pricePerSqFt =
              b.pricePerSqFt ||
              mergedProfile.baseRateSqFt ||
              (b.amountTotal ? b.amountTotal / totalSqFt : 0);

            const included =
              mergedProfile.whatsIncluded && mergedProfile.whatsIncluded.length
                ? mergedProfile.whatsIncluded
                : [
                    "Materials and labor",
                    "Cleanup and debris removal",
                    `${b.warrantyYears}-year workmanship warranty`,
                    "Permit handling",
                  ];

            return {
              id: b.id,
              installerId: b.installerId,
              jobId: b.jobId,

              contractor: mergedProfile.companyName || "Installer",
              contactName: mergedProfile.contactName || "Contact",
              avatar: mergedProfile.avatar,
              rating: mergedProfile.rating || 4.6,
              reviews: mergedProfile.reviews || 25,
              pricePerSqFt,
              price: b.amountTotal,
              timeline:
                mergedProfile.baseTimeline || `${b.timelineDays || 5} days`,
              warranty: `${b.warrantyYears} years`,
              phone: mergedProfile.phone,
              email: mergedProfile.email,
              address: mergedProfile.address,
              specialties: mergedProfile.specialties || ["Roofing"],
              verified: mergedProfile.verified,
              premium: (mergedProfile.rating || 0) > 4.7,
              experience: `${mergedProfile.experienceYears || 5} years`,
              completedJobs:
                mergedProfile.completedJobs ??
                100 + Math.floor(Math.random() * 200),
              responseTime: "3 hours",
              materials: b.materials,
              included,
            };
          });

          setBids(transformed);
        };

        enhanceWithSupabase();
      }
    } else {
      // Demo mode - load from localStorage saved roof detection
      const savedRoofData = localStorage.getItem("roofDetectionData");
      if (savedRoofData) {
        try {
          const parsedData = JSON.parse(savedRoofData);
          setRoofData(parsedData);
        } catch (e) {}
      }
      // Fallback demo bids are handled below in useMemo
    }
  }, [jobId]);

  const demoBids = useMemo(() => {
    if (mode === "job" && bids.length) return [];
    // Demo bids when not viewing a specific job
    const totalSqFt = (() => {
      if (!roofData || !roofData.totalArea) return 2500;
      const areaString = roofData.totalArea.toString();
      const numberMatch = areaString.match(/[\d,]+/);
      if (numberMatch) return parseInt(numberMatch[0].replace(/,/g, ""), 10);
      return 2500;
    })();
    const items = [
      {
        id: "d1",
        contractor: "Elite Roofing Co.",
        contactName: "Mike Johnson",
        avatar:
          "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
        rating: 4.9,
        reviews: 127,
        pricePerSqFt: 5.5,
        timeline: "3-5 days",
        warranty: "15 years",
        phone: "(555) 123-4567",
        email: "mike@eliteroofing.com",
        address: "123 Oak St",
        specialties: ["Metal Roofing", "Storm Damage"],
        verified: true,
        premium: true,
        experience: "12 years",
        completedJobs: 450,
        responseTime: "2 hours",
        materials: "Premium Metal Shingles",
        included: [
          "Materials & labor",
          "Debris removal",
          "Permit handling",
          "15-yr warranty",
        ],
      },
      {
        id: "d2",
        contractor: "Summit Roof Solutions",
        contactName: "Sarah Williams",
        avatar:
          "https://images.unsplash.com/photo-1494790108755-2616b332e234?w=80&h=80&fit=crop&crop=face",
        rating: 4.8,
        reviews: 89,
        pricePerSqFt: 4.25,
        timeline: "4-6 days",
        warranty: "12 years",
        phone: "(555) 987-6543",
        email: "sarah@summitroofs.com",
        address: "456 Pine Ave",
        specialties: ["Asphalt Shingles", "Repairs"],
        verified: true,
        premium: false,
        experience: "8 years",
        completedJobs: 320,
        responseTime: "4 hours",
        materials: "High-Grade Asphalt",
        included: [
          "Materials & install",
          "Site cleanup",
          "Basic permits",
          "12-yr warranty",
        ],
      },
      {
        id: "d3",
        contractor: "Pro Roofing Services",
        contactName: "David Chen",
        avatar:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
        rating: 4.7,
        reviews: 156,
        pricePerSqFt: 7.85,
        timeline: "2-4 days",
        warranty: "20 years",
        phone: "(555) 456-7890",
        email: "david@proroofing.com",
        address: "789 Elm Dr",
        specialties: ["Premium Materials", "Commercial"],
        verified: true,
        premium: true,
        experience: "15 years",
        completedJobs: 680,
        responseTime: "1 hour",
        materials: "Architectural Shingles",
        included: [
          "Premium materials",
          "Express install",
          "Full permit service",
          "20-yr warranty",
        ],
      },
    ];
    return items.map((i) => ({
      ...i,
      price: Math.round(i.pricePerSqFt * totalSqFt),
    }));
  }, [mode, roofData, bids.length]);

  const computedBids = useMemo(() => {
    const arr = mode === "job" ? bids : demoBids;
    const sorted = [...arr].sort((a, b) => {
      if (sortBy === "price") return (a.price || 0) - (b.price || 0);
      if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sortBy === "timeline")
        return parseInt(a.timeline) - parseInt(b.timeline);
      return 0;
    });
    return sorted.filter((b) => {
      if (filterBy === "premium") return b.premium;
      if (filterBy === "budget") return (b.price || 0) < 10000;
      return true;
    });
  }, [mode, bids, demoBids, sortBy, filterBy]);

  const selectBid = (bid) => {
    setSelectedBid(bid);

    try {
      // Persist minimal selection identifiers so ConfirmInstallationPage can load real data
      const idPayload = {
        bidId: bid.id,
        installerId: bid.installerId,
        jobId: bid.jobId || jobId,
        selectedAt: new Date().toISOString(),
      };
      localStorage.setItem("rb_selected_bid", JSON.stringify(idPayload));

      // Also persist the full card details the homeowner actually saw/selected
      const contractorPayload = {
        installerId: bid.installerId,
        jobId: bid.jobId || jobId,
        contractor: bid.contractor,
        contactName: bid.contactName,
        avatar: bid.avatar,
        rating: bid.rating,
        reviews: bid.reviews,
        phone: bid.phone,
        email: bid.email,
        address: bid.address,
        specialties: bid.specialties,
        verified: bid.verified,
        experience: bid.experience,
        completedJobs: bid.completedJobs,
        selectedBid: {
          price: bid.price,
          timeline: bid.timeline,
          warranty: bid.warranty,
          materials: bid.materials,
          included: bid.included,
        },
      };
      localStorage.setItem(
        "rb_selected_contractor",
        JSON.stringify(contractorPayload)
      );
    } catch (e) {
      // ignore storage errors, still navigate
    }

    // Navigate to confirmation page with identifiers so it can hydrate from data layer
    const params = [];
    // console.log(jobId,"details122 : ",bid);

    if (bid.jobId || jobId)
      params.push(`jobId=${encodeURIComponent(bid.jobId || jobId)}`);
    if (bid.id) params.push(`bidId=${encodeURIComponent(bid.id)}`);
    const query = params.length ? `?${params.join("&")}` : "";

    window.location.href = `/confirm-installation${query}`;
  };

  return (
    <AdvancedRoofBidderHeader>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <SortFilterControls
            sortBy={sortBy}
            setSortBy={setSortBy}
            filterBy={filterBy}
            setFilterBy={setFilterBy}
          />

          <PropertyDetailsCard roofData={roofData} />

          <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
            {computedBids.map((bid, index) => (
              <BidCard
                key={bid.id}
                bid={bid}
                index={index}
                roofData={roofData}
                onSelect={selectBid}
              />
            ))}
          </div>

          {computedBids.length === 0 && <NoBidsEmptyState />}
        </div>
      </div>
    </AdvancedRoofBidderHeader>
  );
};

export default CompareBidsPage;
